import React, { useMemo } from 'react';
import { Toolbar, Typography, Container, Box, Button, AppBar, Stack, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import getTheme from './theme';
import getLiveTheme from './modules/quizapp/liveTheme';
import { ThemeModeProvider } from './contexts/ThemeModeContext';
import useThemeMode from './hooks/useThemeMode';
import ThemeToggleButton from './components/ThemeToggleButton';
import { Route, Routes, Link as RouteLink } from 'react-router-dom';
import Writings from './pages/writings';
import Projects from './pages/projects';
import Home from './pages/home';
import registry from './modules/registry';
import HostHome from './modules/quizapp/HostHome';
import HostSession from './modules/quizapp/HostSession';
import PastSessions from './modules/quizapp/PastSessions';
import StudentJoin from './modules/quizapp/StudentJoin';
import HostAuthGate from './modules/quizapp/HostAuthGate';

// liveTheme is built from the sitewide theme via createTheme(theme, overrides),
// so the projector/phone screens get their own warm palette without touching
// the portfolio's black/white brand elsewhere.
function LiveLayout() {
  const { mode } = useThemeMode();
  const liveTheme = useMemo(() => getLiveTheme(mode), [mode]);

  return (
    <ThemeProvider theme={liveTheme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Routes>
          <Route element={<HostAuthGate />}>
            <Route path="host" element={<HostHome />} />
            <Route path="host/sessions" element={<PastSessions />} />
            <Route path="host/:sessionId" element={<HostSession />} />
          </Route>
          <Route path=":joinCode" element={<StudentJoin />} />
        </Routes>
      </Box>
    </ThemeProvider>
  );
}

// The portfolio chrome. /live/* deliberately renders outside this: site nav has
// no business on a projector or on a student's phone mid-quiz.
function SiteLayout() {
  return (
    <>
      <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: 80 }}>
            <Typography
              variant="h6"
              component={RouteLink}
              to="/"
              sx={{
                flexGrow: 1,
                textDecoration: 'none',
                color: 'text.primary',
                fontWeight: 800,
                letterSpacing: '-0.5px',
                fontSize: '1.25rem'
              }}
            >
              MAHENDRA CHOUHAN
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button component={RouteLink} to="/" color="inherit">Home</Button>
              <Button component={RouteLink} to="/projects" color="inherit">Projects</Button>
              <Button component={RouteLink} to="/writings" color="inherit">Writings</Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Box>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/writings" element={<Writings />} />
          <Route path="/projects" element={<Projects />} />
          {registry.map(({ id, href, Page }) => <Route key={id} path={href} element={<Page />} />)}
        </Routes>
      </Box>
    </>
  );
}

// Mounted once here, outside the site/live split, so the toggle shows up on
// every route (including /live/*) without being wired into each layout.
function ThemedApp() {
  const { mode } = useThemeMode();
  const muiTheme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <ThemeToggleButton sx={{ position: 'fixed', top: 12, right: 12, zIndex: (theme) => theme.zIndex.appBar + 1 }} />
      <Routes>
        <Route path="/live/*" element={<LiveLayout />} />
        <Route path="*" element={<SiteLayout />} />
      </Routes>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  );
}

export default App;
